import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

/* ─── helpers ─── */
const fmt     = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtSign = (n) => (n >= 0 ? "+" : "") + fmt(n);
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

const downloadCSV = (filename, rows, headers) => {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ─── badges ─── */
const betTypeMeta = {
  match_bet:     { label: "Match Bet",     bg: "#eff6ff", color: "#2563eb" },
  question_bet:  { label: "Question Bet",  bg: "#f0fdf4", color: "#16a34a" },
  selection_bet: { label: "Selection Bet", bg: "#fdf4ff", color: "#9333ea" },
};
const statusMeta = {
  won:      { bg: "#dcfce7", color: "#16a34a" },
  winner:   { bg: "#dcfce7", color: "#16a34a" },
  approved: { bg: "#dcfce7", color: "#16a34a" },
  lost:     { bg: "#fee2e2", color: "#dc2626" },
  loser:    { bg: "#fee2e2", color: "#dc2626" },
  rejected: { bg: "#fee2e2", color: "#dc2626" },
  refunded: { bg: "#fef9c3", color: "#92400e" },
  refund:   { bg: "#fef9c3", color: "#92400e" },
  pending:  { bg: "#fef9c3", color: "#92400e" },
};
const statusBadge = (s = "") => {
  const m = statusMeta[s.toLowerCase()] || { bg: "#f1f5f9", color: "#64748b" };
  return <span style={{ ...ts.badge, ...m }}>{s || "—"}</span>;
};
const betTypeBadge = (t = "") => {
  const m = betTypeMeta[t] || { label: t, bg: "#f1f5f9", color: "#64748b" };
  return <span style={{ ...ts.badge, background: m.bg, color: m.color }}>{m.label}</span>;
};
const dirBadge = (dir) => (
  <span style={{
    ...ts.badge,
    background: dir === "credit" ? "#dcfce7" : "#fee2e2",
    color:      dir === "credit" ? "#16a34a" : "#dc2626",
  }}>
    {dir === "credit" ? "▲ Credit" : "▼ Debit"}
  </span>
);

const TABS = [
  { key: "overview",    label: "Overview",    icon: "solar:chart-2-bold-duotone" },
  { key: "ledger",      label: "Wallet Ledger", icon: "solar:wallet-bold-duotone" },
  { key: "deposits",    label: "Deposits",    icon: "solar:arrow-down-bold-duotone" },
  { key: "withdrawals", label: "Withdrawals", icon: "solar:arrow-up-bold-duotone" },
  { key: "section_a",   label: "Section A Bets", icon: "solar:checklist-bold-duotone" },
  { key: "section_c",   label: "Section C Bets", icon: "solar:question-circle-bold-duotone" },
];

export const User360ReportLayer = () => {
  const [users,        setUsers]        = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQuery,    setUserQuery]    = useState("");
  const [dropOpen,     setDropOpen]     = useState(false);
  const [searching,    setSearching]    = useState(false);
  const debounceRef = useRef(null);

  const [betsData,   setBetsData]   = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [loading,    setLoading]    = useState(false);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [tab,      setTab]      = useState("overview");
  const [search,   setSearch]   = useState("");

  /* ── initial user list ── */
  useEffect(() => {
    http.get("reports_user_bets").then((res) => {
      if (res.data?.status) setUsers(res.data.users || []);
    }).catch(() => {});
  }, []);

  /* ── debounced user search ── */
  const handleUserQuery = (e) => {
    const val = e.target.value;
    setUserQuery(val);
    setDropOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await http.get("add_user", { params: { search: val } });
        if (res.data?.status) setUsers(res.data.data || []);
      } finally { setSearching(false); }
    }, 350);
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    setUserQuery(u.name + (u.mobile ? `  •  ${u.mobile}` : ""));
    setDropOpen(false);
    setBetsData(null);
    setWalletData(null);
  };

  const clearUser = () => {
    setSelectedUser(null);
    setUserQuery("");
    setBetsData(null);
    setWalletData(null);
    setTab("overview");
    setSearch("");
  };

  /* ── load full 360 report (bets + wallet in parallel) ── */
  const loadReport = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const betParams = { user_id: selectedUser.id };
      if (dateFrom) betParams.date_from = dateFrom;
      if (dateTo)   betParams.date_to   = dateTo;

      const [betsRes, walletRes] = await Promise.all([
        http.get("reports_user_bets",    { params: betParams }),
        http.get("user_wallet_history",  { params: { user_id: selectedUser.id } }),
      ]);

      if (betsRes.data?.status)   setBetsData(betsRes.data);
      else toast.error(betsRes.data?.msg || "Bets report failed");

      if (walletRes.data?.status) setWalletData(walletRes.data);
      else toast.error(walletRes.data?.msg || "Wallet history failed");
    } catch {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  }, [selectedUser, dateFrom, dateTo]);

  useEffect(() => { if (selectedUser) loadReport(); }, [loadReport]);

  /* ── derived rows per tab (with search filter) ── */
  const currentRows = useMemo(() => {
    if (!betsData && !walletData) return [];
    let src = [];
    if (tab === "ledger")       src = walletData?.ledger      || [];
    if (tab === "deposits")     src = walletData?.deposits    || [];
    if (tab === "withdrawals")  src = walletData?.withdrawals || [];
    if (tab === "section_a")    src = betsData?.section_a     || [];
    if (tab === "section_c")    src = betsData?.section_c     || [];
    if (!search.trim()) return src;
    const v = search.toLowerCase();
    return src.filter((r) =>
      Object.values(r).some((val) => String(val ?? "").toLowerCase().includes(v))
    );
  }, [tab, search, betsData, walletData]);

  const tabCount = (key) => {
    if (key === "ledger")      return walletData?.ledger?.length       || 0;
    if (key === "deposits")    return walletData?.deposits?.length     || 0;
    if (key === "withdrawals") return walletData?.withdrawals?.length  || 0;
    if (key === "section_a")   return betsData?.section_a?.length      || 0;
    if (key === "section_c")   return betsData?.section_c?.length      || 0;
    return 0;
  };

  /* ── export current tab ── */
  const handleExport = () => {
    if (tab === "overview") { toast.info("Switch to a data tab to export"); return; }
    if (!currentRows.length) { toast.warn("Nothing to export"); return; }
    const headersMap = {
      ledger:      ["id","ref_type","amount","balance_before","balance_after","txn_direction","remark","created_at"],
      deposits:    ["id","amount","status","utr_number","created_at"],
      withdrawals: ["id","amount","status","created_at"],
      section_a:   ["bet_id","bet_type","match_name","question_title","side","amount","win_amount","status","created_at"],
      section_c:   ["bet_id","bet_type","match_name","question_title","selected_option","amount","win_amount","status","created_at"],
    };
    downloadCSV(
      `User360_${selectedUser?.name || "user"}_${tab}_${new Date().toISOString().slice(0,10)}.csv`,
      currentRows, headersMap[tab]
    );
    toast.success("CSV exported");
  };

  /* ── derived wallet totals for overview ── */
  const walletTotals = useMemo(() => {
    if (!walletData) return null;
    const sumBy = (arr, status, field = "amount") =>
      arr.filter((r) => (r.status || "").toLowerCase() === status)
         .reduce((sum, r) => sum + Number(r[field] || 0), 0);
    return {
      balance:            walletData.user_info?.wallet_balance || 0,
      deposits_approved:  sumBy(walletData.deposits || [], "approved"),
      deposits_pending:   sumBy(walletData.deposits || [], "pending"),
      withdraw_approved:  sumBy(walletData.withdrawals || [], "approved"),
      withdraw_pending:   sumBy(walletData.withdrawals || [], "pending"),
      ledger_entries:     (walletData.ledger || []).length,
    };
  }, [walletData]);

  const summary   = betsData?.summary || {};
  const userInfo  = betsData?.user_info || walletData?.user_info || selectedUser || {};

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerCard}>
        <div style={s.headerLeft}>
          <div style={s.iconWrap}>
            <Icon icon="solar:user-id-bold-duotone" style={{ fontSize: 26 }} />
          </div>
          <div>
            <h4 style={s.pageTitle}>User 360 Report</h4>
            <p style={s.pageSub}>Complete wallet, transactions & bet history of any user</p>
          </div>
        </div>
        {(betsData || walletData) && (
          <button style={s.exportBtn} onClick={handleExport}>
            <Icon icon="solar:file-download-bold" style={{ fontSize: 16 }} />
            Export {tab === "overview" ? "" : tab.replace("_", " ")}
          </button>
        )}
      </div>

      {/* ── User Selector + Filters ── */}
      <div style={s.filterCard}>
        <div style={{ flex: "1 1 280px", position: "relative", minWidth: 240 }}>
          <label style={s.label}>Select User (UID / Name / Mobile)</label>
          {!selectedUser ? (
            <>
              <input
                style={s.input} placeholder="Search by UID, name or mobile…"
                value={userQuery} onChange={handleUserQuery}
                onFocus={() => setDropOpen(true)}
                onBlur={() => setTimeout(() => setDropOpen(false), 150)}
                autoComplete="off"
              />
              {dropOpen && (searching || users.length > 0) && (
                <div style={s.dropdown}>
                  {searching && (
                    <div style={{ padding: "9px 14px", color: "#94a3b8", fontSize: "0.8rem" }}>
                      <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, marginRight: 6 }} />
                      Searching…
                    </div>
                  )}
                  {!searching && users.slice(0, 25).map((u) => (
                    <div key={u.id} style={s.dropItem}
                      onMouseDown={() => selectUser(u)}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</span>
                      <span style={{ fontSize: "0.75rem", color: "#94a3b8", marginLeft: 8 }}>
                        {u.mobile} <span style={{ color: "#cbd5e1" }}>· UID {u.uid || u.id}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={s.userCard}>
              <div style={s.userAvatar}>
                <Icon icon="solar:user-bold-duotone" style={{ fontSize: 20, color: "#16a34a" }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#14532d", fontSize: "0.88rem" }}>{userInfo.name}</div>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                  {userInfo.mobile || "—"} · UID {userInfo.uid || userInfo.id} {userInfo.email ? `· ${userInfo.email}` : ""}
                </div>
              </div>
              <button style={s.clearBtn} onClick={clearUser} title="Clear user">
                <Icon icon="radix-icons:cross-2" style={{ fontSize: 13 }} />
              </button>
            </div>
          )}
        </div>

        <div style={{ flex: "0 0 150px" }}>
          <label style={s.label}>From Date</label>
          <input type="date" style={s.input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div style={{ flex: "0 0 150px" }}>
          <label style={s.label}>To Date</label>
          <input type="date" style={s.input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        {selectedUser && (
          <div style={{ alignSelf: "flex-end" }}>
            <button style={s.loadBtn} onClick={loadReport} disabled={loading}>
              {loading
                ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                : <Icon icon="solar:refresh-bold" style={{ fontSize: 15 }} />}
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* ── No user selected ── */}
      {!selectedUser && (
        <div style={s.emptyState}>
          <Icon icon="solar:user-id-bold-duotone" style={{ fontSize: 56, color: "#cbd5e1", display: "block", margin: "0 auto 12px" }} />
          <div style={{ fontWeight: 700, color: "#94a3b8", fontSize: "0.95rem" }}>Select a user above to view the full 360° report</div>
          <div style={{ fontSize: "0.78rem", color: "#cbd5e1", marginTop: 6 }}>Wallet · Deposits · Withdrawals · Section A / C bets · P&amp;L</div>
        </div>
      )}

      {/* ── Summary Tiles ── */}
      {selectedUser && (betsData || walletData) && (
        <div style={s.summaryGrid}>
          {walletTotals && (
            <div style={{ ...s.sumCard, ...s.balanceCard }}>
              <div style={{ ...s.sumIcon, background: "#ffffff30", color: "#fff" }}>
                <Icon icon="solar:wallet-money-bold-duotone" style={{ fontSize: 22 }} />
              </div>
              <div>
                <div style={{ ...s.sumValue, color: "#fff" }}>{fmt(walletTotals.balance)}</div>
                <div style={{ ...s.sumLabel, color: "#dcfce7" }}>Wallet Balance</div>
              </div>
            </div>
          )}
          {[
            { label: "Total Bets",     value: (summary.total_bets || 0).toLocaleString(),    icon: "solar:gamepad-bold-duotone", color: "#3b82f6" },
            { label: "Invested",       value: fmt(summary.total_invested),                   icon: "solar:hand-money-bold-duotone", color: "#8b5cf6" },
            { label: "Won",            value: fmt(summary.total_won),                        icon: "solar:cup-bold-duotone",     color: "#16a34a" },
            { label: "Refund",         value: fmt(summary.total_refund),                     icon: "solar:refresh-bold-duotone", color: "#f59e0b" },
            { label: "Net P&L",        value: fmtSign(summary.net_pnl || 0),                 icon: "solar:chart-2-bold-duotone", color: (summary.net_pnl || 0) >= 0 ? "#16a34a" : "#dc2626" },
            walletTotals && { label: "Deposits (Approved)",   value: fmt(walletTotals.deposits_approved),  icon: "solar:arrow-down-bold-duotone", color: "#0ea5e9" },
            walletTotals && { label: "Withdrawn (Approved)",  value: fmt(walletTotals.withdraw_approved),  icon: "solar:arrow-up-bold-duotone",   color: "#db2777" },
          ].filter(Boolean).map((c) => (
            <div key={c.label} style={s.sumCard}>
              <div style={{ ...s.sumIcon, background: `${c.color}18`, color: c.color }}>
                <Icon icon={c.icon} style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ ...s.sumValue, color: c.color }}>{c.value}</div>
                <div style={s.sumLabel}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabs + Content ── */}
      {selectedUser && (betsData || walletData) && (
        <>
          <div style={s.tabRow}>
            {TABS.map((t) => (
              <button key={t.key}
                style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActive : {}) }}
                onClick={() => { setTab(t.key); setSearch(""); }}
              >
                <Icon icon={t.icon} style={{ fontSize: 14 }} />
                {t.label}
                {t.key !== "overview" && (
                  <span style={{ ...s.tabCount, background: tab === t.key ? "rgba(255,255,255,0.25)" : "#f1f5f9" }}>
                    {tabCount(t.key)}
                  </span>
                )}
              </button>
            ))}
            {tab !== "overview" && (
              <div style={{ marginLeft: "auto", position: "relative" }}>
                <Icon icon="ion:search-outline" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8", pointerEvents: "none" }} />
                <input style={{ ...s.input, paddingLeft: 32, width: 220 }} placeholder="Filter rows…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            )}
          </div>

          {/* Overview tab */}
          {tab === "overview" && (
            <div style={s.overviewGrid}>
              <div style={s.overviewCard}>
                <div style={s.overviewTitle}><Icon icon="solar:user-bold-duotone" /> Profile</div>
                <div style={s.kvRow}><span style={s.kvKey}>Name</span><span style={s.kvVal}>{userInfo.name || "—"}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>UID</span><span style={s.kvVal}>{userInfo.uid || userInfo.id}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Mobile</span><span style={s.kvVal}>{userInfo.mobile || "—"}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Email</span><span style={s.kvVal}>{userInfo.email || "—"}</span></div>
              </div>

              <div style={s.overviewCard}>
                <div style={s.overviewTitle}><Icon icon="solar:wallet-bold-duotone" /> Wallet Snapshot</div>
                <div style={s.kvRow}><span style={s.kvKey}>Balance</span><span style={{ ...s.kvVal, color: "#16a34a", fontWeight: 800 }}>{fmt(walletTotals?.balance)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Deposits (Approved)</span><span style={s.kvVal}>{fmt(walletTotals?.deposits_approved)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Deposits (Pending)</span><span style={s.kvVal}>{fmt(walletTotals?.deposits_pending)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Withdraw (Approved)</span><span style={s.kvVal}>{fmt(walletTotals?.withdraw_approved)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Withdraw (Pending)</span><span style={s.kvVal}>{fmt(walletTotals?.withdraw_pending)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Ledger Entries</span><span style={s.kvVal}>{walletTotals?.ledger_entries || 0}</span></div>
              </div>

              <div style={s.overviewCard}>
                <div style={s.overviewTitle}><Icon icon="solar:chart-2-bold-duotone" /> Betting Summary</div>
                <div style={s.kvRow}><span style={s.kvKey}>Total Bets</span><span style={s.kvVal}>{(summary.total_bets || 0).toLocaleString()}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Invested</span><span style={s.kvVal}>{fmt(summary.total_invested)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Won</span><span style={{ ...s.kvVal, color: "#16a34a" }}>{fmt(summary.total_won)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Refund</span><span style={s.kvVal}>{fmt(summary.total_refund)}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Net P&amp;L</span>
                  <span style={{ ...s.kvVal, fontWeight: 800, color: (summary.net_pnl || 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                    {fmtSign(summary.net_pnl || 0)}
                  </span>
                </div>
                <div style={s.kvRow}><span style={s.kvKey}>Section A count</span><span style={s.kvVal}>{betsData?.section_a?.length || 0}</span></div>
                <div style={s.kvRow}><span style={s.kvKey}>Section C count</span><span style={s.kvVal}>{betsData?.section_c?.length || 0}</span></div>
              </div>
            </div>
          )}

          {/* Table tabs */}
          {tab !== "overview" && (
            <div style={s.card}>
              {loading ? (
                <div style={s.emptyCell}>
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
                  Loading…
                </div>
              ) : currentRows.length === 0 ? (
                <div style={s.emptyCell}>
                  <Icon icon="solar:box-minimalistic-bold-duotone" style={{ fontSize: 40, color: "#cbd5e1", display: "block", margin: "0 auto 10px" }} />
                  No records found
                </div>
              ) : (
                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        {tab === "ledger" && <>
                          <th style={s.th}>#</th>
                          <th style={s.th}>Type</th>
                          <th style={s.th}>Direction</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Amount</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Bal Before</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Bal After</th>
                          <th style={s.th}>Remark</th>
                          <th style={s.th}>Date</th>
                        </>}
                        {tab === "deposits" && <>
                          <th style={s.th}>#</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Amount</th>
                          <th style={s.th}>UTR</th>
                          <th style={s.th}>Status</th>
                          <th style={s.th}>Date</th>
                        </>}
                        {tab === "withdrawals" && <>
                          <th style={s.th}>#</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Amount</th>
                          <th style={s.th}>Status</th>
                          <th style={s.th}>Date</th>
                        </>}
                        {(tab === "section_a" || tab === "section_c") && <>
                          <th style={s.th}>#</th>
                          <th style={s.th}>Type</th>
                          <th style={s.th}>Match</th>
                          <th style={s.th}>Question</th>
                          <th style={s.th}>Side / Option</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Invested</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Won</th>
                          <th style={s.th}>Status</th>
                          <th style={s.th}>Date</th>
                        </>}
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.map((row, idx) => (
                        <tr key={idx}
                          style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                          onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                        >
                          {tab === "ledger" && <>
                            <td style={s.td}><span style={ts.serial}>{idx + 1}</span></td>
                            <td style={s.td}><span style={ts.refType}>{row.ref_type || "—"}</span></td>
                            <td style={s.td}>{dirBadge(row.txn_direction)}</td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <span style={{ fontWeight: 700, color: row.txn_direction === "credit" ? "#16a34a" : "#dc2626" }}>
                                {row.txn_direction === "credit" ? "+" : "-"}{fmt(row.amount)}
                              </span>
                            </td>
                            <td style={{ ...s.td, textAlign: "right" }}><span style={ts.mono}>{fmt(row.balance_before)}</span></td>
                            <td style={{ ...s.td, textAlign: "right" }}><span style={{ ...ts.mono, fontWeight: 700 }}>{fmt(row.balance_after)}</span></td>
                            <td style={s.td}><span style={{ fontSize: "0.78rem", color: "#64748b" }}>{row.remark || "—"}</span></td>
                            <td style={s.td}><span style={ts.date}>{fmtDate(row.created_at)}</span></td>
                          </>}
                          {tab === "deposits" && <>
                            <td style={s.td}><span style={ts.serial}>{idx + 1}</span></td>
                            <td style={{ ...s.td, textAlign: "right" }}><span style={{ fontWeight: 700, color: "#16a34a" }}>{fmt(row.amount)}</span></td>
                            <td style={s.td}><span style={ts.mono}>{row.utr_number || "—"}</span></td>
                            <td style={s.td}>{statusBadge(row.status)}</td>
                            <td style={s.td}><span style={ts.date}>{fmtDate(row.created_at)}</span></td>
                          </>}
                          {tab === "withdrawals" && <>
                            <td style={s.td}><span style={ts.serial}>{idx + 1}</span></td>
                            <td style={{ ...s.td, textAlign: "right" }}><span style={{ fontWeight: 700, color: "#dc2626" }}>{fmt(row.amount)}</span></td>
                            <td style={s.td}>{statusBadge(row.status)}</td>
                            <td style={s.td}><span style={ts.date}>{fmtDate(row.created_at)}</span></td>
                          </>}
                          {(tab === "section_a" || tab === "section_c") && <>
                            <td style={s.td}><span style={ts.serial}>{idx + 1}</span></td>
                            <td style={s.td}>{betTypeBadge(row.bet_type)}</td>
                            <td style={s.td}><span style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.82rem" }}>{row.match_name || "—"}</span></td>
                            <td style={s.td}><span style={{ fontSize: "0.8rem", color: "#475569" }}>{row.question_title || "—"}</span></td>
                            <td style={s.td}>
                              {row.side || row.selected_option
                                ? <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#475569" }}>{row.side || row.selected_option}</span>
                                : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <span style={{ fontWeight: 700, color: "#8b5cf6" }}>{fmt(row.amount)}</span>
                            </td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <span style={{ fontWeight: 700, color: row.win_amount > 0 ? "#16a34a" : "#94a3b8" }}>
                                {row.win_amount > 0 ? fmt(row.win_amount) : "—"}
                              </span>
                            </td>
                            <td style={s.td}>{statusBadge(row.status)}</td>
                            <td style={s.td}><span style={ts.date}>{fmtDate(row.created_at)}</span></td>
                          </>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  headerCard: { background: "#fff", borderRadius: 16, padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  iconWrap: { width: 50, height: 50, borderRadius: 14, background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.4rem", color: "#0f172a" },
  pageSub:  { margin: "3px 0 0", fontSize: "0.82rem", color: "#64748b" },
  exportBtn:{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" },

  filterCard: { background: "#fff", borderRadius: 14, padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", border: "1px solid #e8edf2", marginBottom: 20 },
  label: { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "8px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.84rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  loadBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, color: "#fff", fontSize: "0.84rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },

  dropdown: { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 260, overflowY: "auto", marginTop: 2 },
  dropItem: { padding: "9px 14px", cursor: "pointer", background: "#fff", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 4 },

  userCard:   { display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: 10 },
  userAvatar: { width: 34, height: 34, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  clearBtn:   { background: "#fee2e2", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626", flexShrink: 0 },

  emptyState: { background: "#fff", borderRadius: 16, padding: "60px 20px", textAlign: "center", border: "1px solid #e8edf2" },

  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 },
  sumCard:    { background: "#fff", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, border: "1px solid #e8edf2" },
  balanceCard:{ background: "linear-gradient(135deg,#16632b,#1da144)", border: "none" },
  sumIcon:    { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sumValue:   { fontSize: "1.18rem", fontWeight: 800, lineHeight: 1 },
  sumLabel:   { fontSize: "0.66rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 },

  tabRow: { display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  tabBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 10, cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, color: "#475569", fontFamily: "inherit" },
  tabActive: { background: "linear-gradient(135deg,#0f4c25,#16632b)", color: "#fff", border: "none" },
  tabCount: { marginLeft: 4, padding: "2px 7px", borderRadius: 100, fontSize: "0.67rem", fontWeight: 700 },

  overviewGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 },
  overviewCard: { background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid #e8edf2" },
  overviewTitle:{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800, fontSize: "0.95rem", color: "#0f172a", marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" },
  kvRow:{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px dashed #f1f5f9" },
  kvKey:{ fontSize: "0.78rem", color: "#64748b", fontWeight: 500 },
  kvVal:{ fontSize: "0.84rem", color: "#0f172a", fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", overflow: "hidden" },
  tableWrap: { overflowX: "auto", maxHeight: "62vh", overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.81rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 },
  td: { padding: "10px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
};

const ts = {
  badge:   { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 700, textTransform: "capitalize" },
  serial:  { fontSize: "0.73rem", color: "#94a3b8" },
  refType: { fontFamily: "monospace", fontSize: "0.75rem", color: "#475569", fontWeight: 600 },
  mono:    { fontFamily: "monospace", fontSize: "0.78rem", color: "#475569" },
  date:    { fontSize: "0.77rem", color: "#64748b" },
};
